import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../App';
import { projectsAPI, jobCardsAPI, usersAPI, dashboardAPI, configAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import appLogo from '../assets/logo.png';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from './ui/command';
import { Textarea } from './ui/textarea';
import WorkDetailsForm from './WorkDetailsForm';
import OperatorEditHistory from './OperatorEditHistory';
import PressingBalance from './PressingBalance';
import { pressingProgress } from '../utils/pressing';
import WorkLogHistory from './WorkLogHistory';
import { BillDialog } from './BillView';
import FloorStatus from './FloorStatus';
import JobCardPressingForm from './JobCardPressingForm';
import BOMManager from './BOMManager';
import CoordinatorTaskManager from './CoordinatorTaskManager';
import DateFilter from './DateFilter';
import {
  LayoutDashboard,
  FolderKanban,
  FileText,
  Plus,
  LogOut,
  ChevronRight,
  Search,
  CheckCircle,
  Scissors,
  Layers,
  CircleDot,
  Eye,
  Download,
  FileSpreadsheet,
  UserPlus,
  Edit,
  Package,
  ClipboardCheck,
  Camera,
  Trash2,
  ImageIcon,
  X,
  Printer,
  Clock,
  XCircle,
  RotateCcw,
  CornerUpLeft,
  Filter,
  Save,
  Highlighter
} from 'lucide-react';

import NotificationBell from './NotificationBell';
import { printJobCard } from '../utils/printUtils';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const CoordinatorDashboard = () => {
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [projects, setProjects] = useState([]);
  const [jobCards, setJobCards] = useState([]);
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog states
  const [showProjectDialog, setShowProjectDialog] = useState(false);
  const [showPressingDialog, setShowPressingDialog] = useState(false);
  const [showWorkDetailsDialog, setShowWorkDetailsDialog] = useState(false);
  const [showClientDialog, setShowClientDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedJobCard, setSelectedJobCard] = useState(null);
  const [viewJobCard, setViewJobCard] = useState(null);
  
  // Cancel dialog state
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancellingJobCard, setCancellingJobCard] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  
  // Form refs
  const jobCardFormRef = useRef();
  const workDetailsFormRef = useRef();
  const pressingImageInputRef = useRef();
  
  // Pressing image states
  const [pressingImages, setPressingImages] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [viewingPressingImage, setViewingPressingImage] = useState(null);
  
  // Estimate preview states
  const [showEstimatePreview, setShowEstimatePreview] = useState(false);
  const [estimatePreview, setEstimatePreview] = useState(null);
  const [previewJobCardId, setPreviewJobCardId] = useState(null);
  // Read-only bill for a card that has already been sent
  const [billJobCard, setBillJobCard] = useState(null);
  const [sendingEstimate, setSendingEstimate] = useState(false);
  const [breakdownLine, setBreakdownLine] = useState(null);
  
  // Date filter state
  const [dateFilter, setDateFilter] = useState('all');
  const [dateFilterStart, setDateFilterStart] = useState(null);
  const [dateFilterEnd, setDateFilterEnd] = useState(null);
  const [showOnlyMine, setShowOnlyMine] = useState(false);
  
  // Inline edit client name
  const [editingClientName, setEditingClientName] = useState(false);
  const [editClientNameValue, setEditClientNameValue] = useState('');

  // Form states
  const [newProject, setNewProject] = useState({ client_id: '', reference: '', description: '' });
  const [newClient, setNewClient] = useState({ name: '', mobile: '', password: '', company: '', email: '' });
  
  // Stats dialog state
  const [showStatsDialog, setShowStatsDialog] = useState(false);
  const [statsDialogType, setStatsDialogType] = useState(null); // 'active', 'open', 'completed'
  
  // Inline client creation in project dialog
  const [showInlineClientForm, setShowInlineClientForm] = useState(false);
  const [clientSearchOpen, setClientSearchOpen] = useState(false);
  const [inlineNewClient, setInlineNewClient] = useState({ name: '', mobile: '', company: '' });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [projectsData, jobCardsData, clientsData, statsData, configData] = await Promise.all([
        projectsAPI.getAll(),
        jobCardsAPI.getAll(),
        usersAPI.getClients(),
        dashboardAPI.getStats(),
        configAPI.get()
      ]);
      setProjects(projectsData);
      setJobCards(jobCardsData);
      setClients(clientsData);
      setStats(statsData);
      setConfig(configData);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async () => {
    try {
      let clientId = newProject.client_id;
      
      // If creating a new client inline
      if (showInlineClientForm) {
        if (!inlineNewClient.name || !inlineNewClient.mobile) {
          toast({ title: "Error", description: "Client name and mobile are required", variant: "destructive" });
          return;
        }
        
        // Create the client first
        const newClientData = await usersAPI.createClient({
          name: inlineNewClient.name,
          mobile: inlineNewClient.mobile,
          password: process.env.REACT_APP_DEFAULT_CLIENT_PASSWORD || 'changeme',
          role: 'client',
          company: inlineNewClient.company || ''
        });
        
        clientId = newClientData.id;
        toast({ title: "Client Created", description: `New client "${inlineNewClient.name}" created successfully` });
      }
      
      if (!clientId) {
        toast({ title: "Error", description: "Please select or create a client", variant: "destructive" });
        return;
      }
      
      // Create the project with the client
      await projectsAPI.create({ ...newProject, client_id: clientId });
      toast({ title: "Success", description: "New job card created successfully" });
      
      // Reset form states
      setShowProjectDialog(false);
      setNewProject({ client_id: '', reference: '', description: '' });
      setShowInlineClientForm(false);
      setInlineNewClient({ name: '', mobile: '', company: '' });
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to create project", variant: "destructive" });
    }
  };

  const handleUpdatePressingJobCard = async () => {
    if (!jobCardFormRef.current || !selectedJobCard) return;
    const formData = jobCardFormRef.current.getData();
    console.log('[PRESSING] Form data received:', formData);
    try {
      const updateData = {
        reference: formData.reference,
        mobile_no: formData.mob_no,
        delivery_date: formData.delivery_date || null,
        ref_job_card_no: formData.ref_job_card_no,
        work_in_charge: formData.work_in_charge,
        // Map hot_pressing_lines from form to normal_pressing_lines for backend
        // Preserve existing id, status, operator_note, machine_type from DB
        normal_pressing_lines: (formData.hot_pressing_lines || []).map((line, idx) => {
          const existing = (selectedJobCard.normal_pressing_lines || [])[idx] || {};
          return {
            description: line.description,
            ply_qty: parseInt(line.ply_qty) || 0,
            side: parseInt(line.side) || 1,
            details: line.details,
            id: existing.id || line.id,
            status: existing.status || line.status || 'not_started',
            operator_note: existing.operator_note || line.operator_note || null,
            machine_type: existing.machine_type || line.machine_type || null,
            highlighted: !!line.highlighted,
          };
        }),
        veneer_pressing_lines: (formData.veneer_pressing_lines || []).map((line, idx) => {
          const existing = (selectedJobCard.veneer_pressing_lines || [])[idx] || {};
          return {
            description: line.description,
            ply_qty: parseInt(line.ply_qty) || 0,
            side: parseInt(line.side) || 1,
            details: line.details,
            id: existing.id || line.id,
            status: existing.status || line.status || 'not_started',
            operator_note: existing.operator_note || line.operator_note || null,
            machine_type: existing.machine_type || line.machine_type || null,
            highlighted: !!line.highlighted,
          };
        })
      };
      console.log('[PRESSING] Update data to send:', updateData);
      await jobCardsAPI.update(selectedJobCard.id, updateData);
      toast({ title: "Success", description: "Pressing details saved successfully" });
      setShowPressingDialog(false);
      setSelectedJobCard(null);
      fetchData();
    } catch (error) {
      console.error('[PRESSING] Error saving:', error);
      toast({ title: "Error", description: "Failed to save pressing details", variant: "destructive" });
    }
  };

  const handleUploadPressingImage = async (file) => {
    if (!selectedJobCard || !file) return;
    setUploadingImage(true);
    try {
      const result = await jobCardsAPI.uploadPressingImage(selectedJobCard.id, file);
      setPressingImages(prev => [...prev, result.data.url]);
      toast({ title: "Success", description: "Image uploaded" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to upload image", variant: "destructive" });
    } finally {
      setUploadingImage(false);
      if (pressingImageInputRef.current) pressingImageInputRef.current.value = '';
    }
  };

  const handleDeletePressingImage = async (imageUrl) => {
    if (!selectedJobCard) return;
    try {
      await jobCardsAPI.deletePressingImage(selectedJobCard.id, imageUrl);
      setPressingImages(prev => prev.filter(u => u !== imageUrl));
      toast({ title: "Success", description: "Image deleted" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete image", variant: "destructive" });
    }
  };

  const handleCloseJobCard = async (jobCardId) => {
    // Check coordinator tasks before closing
    const jc = jobCards.find(j => j.id === jobCardId);
    if (jc) {
      const completedTasks = (jc.coordinator_tasks || []).filter(t => t.status === 'completed');
      if (completedTasks.length < 3) {
        const missing = ['Drawing', 'Cutting List', 'Re-check'].filter(label => {
          const key = label.toLowerCase().replace(/-/g, '').replace(/ /g, '_');
          return !completedTasks.some(t => t.task_type === key);
        });
        toast({
          title: "Cannot Close Job Card",
          description: `Complete these coordinator tasks first: ${missing.join(', ')}`,
          variant: "destructive"
        });
        return;
      }
      // Check boring operator has updated
      if (!jc.boring_updated) {
        toast({
          title: "Cannot Close Job Card",
          description: "Boring operator has not updated the boring quantity yet.",
          variant: "destructive"
        });
        return;
      }
    }
    try {
      await jobCardsAPI.close(jobCardId);
      toast({ title: "Success", description: "Job card closed" });
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to close job card";
      toast({ title: "Error", description: msg, variant: "destructive" });
    }
  };

  // Pre-check: returns null if ok, or a friendly error message listing missing tasks
  const checkCoordinatorTasksComplete = (jc) => {
    const required = [
      { key: 'drawing', label: 'Drawing' },
      { key: 'cutting_list', label: 'Cutting List' },
      { key: 'recheck', label: 'Re-check' }
    ];
    const completed = (jc?.coordinator_tasks || []).filter(t => t.status === 'completed').map(t => t.task_type);
    const missing = required.filter(r => !completed.includes(r.key)).map(r => r.label);
    if (missing.length > 0) {
      return `Please complete all Coordinator Tasks first. Pending: ${missing.join(', ')}.`;
    }
    return null;
  };

  // Open job card detail and scroll to Coordinator Tasks section (used when tasks are incomplete)
  const openTasksForJobCard = (jc) => {
    setViewJobCard(jc);
    // Wait for the dialog to render, then scroll & flash
    setTimeout(() => {
      const el = document.getElementById('coordinator-tasks-section');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-4', 'ring-amber-300', 'rounded-lg', 'transition');
        setTimeout(() => {
          el.classList.remove('ring-4', 'ring-amber-300');
        }, 2200);
      }
    }, 250);
  };

  const handleSendToAccounts = async (jobCardId) => {
    const jc = jobCards.find(j => j.id === jobCardId);
    const missingMsg = checkCoordinatorTasksComplete(jc);
    if (missingMsg) {
      toast({ title: "Coordinator Tasks incomplete", description: missingMsg + " Opening the tasks section now.", variant: "destructive" });
      openTasksForJobCard(jc);
      return;
    }
    try {
      const preview = await jobCardsAPI.getEstimatePreview(jobCardId);
      setEstimatePreview(preview);
      setPreviewJobCardId(jobCardId);
      setShowEstimatePreview(true);
    } catch (error) {
      const msg = error?.response?.data?.detail || "Failed to generate estimate preview";
      toast({ title: "Error", description: msg, variant: "destructive" });
    }
  };

  const handlePushToProduction = async (jobCardId) => {
    try {
      await jobCardsAPI.pushToProduction(jobCardId);
      toast({ title: "Success", description: "Job card pushed to production - now visible to operators" });
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to push to production", variant: "destructive" });
    }
  };

  const handleSendToBilling = async (jobCardId) => {
    const jc = jobCards.find(j => j.id === jobCardId);
    const missingMsg = checkCoordinatorTasksComplete(jc);
    if (missingMsg) {
      toast({ title: "Coordinator Tasks incomplete", description: missingMsg + " Opening the tasks section now.", variant: "destructive" });
      openTasksForJobCard(jc);
      return;
    }
    try {
      const preview = await jobCardsAPI.getEstimatePreview(jobCardId);
      setEstimatePreview(preview);
      setPreviewJobCardId(jobCardId);
      setShowEstimatePreview(true);
    } catch (error) {
      const msg = error?.response?.data?.detail || "Failed to generate estimate preview";
      toast({ title: "Error", description: msg, variant: "destructive" });
    }
  };

  const handleConfirmSendToAccounts = async () => {
    setSendingEstimate(true);
    try {
      await jobCardsAPI.sendToSupervisor(previewJobCardId, estimatePreview.estimate_lines);
      toast({ title: "Success", description: "Job card sent to supervisor for recheck" });
      setShowEstimatePreview(false);
      setEstimatePreview(null);
      setPreviewJobCardId(null);
      fetchData();
    } catch (error) {
      const msg = error?.response?.data?.detail || "Failed to send to supervisor";
      toast({ title: "Error", description: msg, variant: "destructive" });
    }
    setSendingEstimate(false);
  };

  // A collated line (e.g. pressing) averages its breakdown, so an unpriced
  // sub-line hides inside a plausible-looking non-zero rate. Flag those too.
  const unpricedCount = (line) =>
    (line.breakdown || []).filter(b => !b.rate).length;
  const isUnpriced = (line) => !line.rate || unpricedCount(line) > 0;

  // Pressing still outstanding on the card being sent for review - warn before it
  // leaves the floor (JC 656 went to review with 24 sheets unpressed).
  const sendPressing = pressingProgress(jobCards.find(j => j.id === previewJobCardId));
  const sendPressingLeft = sendPressing.hasPressing ? sendPressing.balance : 0;
  // Pressing items missing from the rate chart are billed at the category's
  // default rate; count them so chart gaps stay visible without blocking.
  const defaultedCount = (line) =>
    (line.breakdown || []).filter(b => b.defaulted).length;

  const handleSaveClientName = async () => {
    if (!editClientNameValue.trim()) {
      toast({ title: "Error", description: "Client name cannot be empty", variant: "destructive" });
      return;
    }
    try {
      await jobCardsAPI.update(viewJobCard.id, { customer_name: editClientNameValue.trim() });
      toast({ title: "Success", description: "Client name updated" });
      setEditingClientName(false);
      setViewJobCard({ ...viewJobCard, customer_name: editClientNameValue.trim() });
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to update client name", variant: "destructive" });
    }
  };

  const handleCancelJobCard = async () => {
    if (!cancellingJobCard || !cancelReason.trim()) {
      toast({ title: "Error", description: "Please provide a cancellation reason", variant: "destructive" });
      return;
    }
    try {
      await jobCardsAPI.cancel(cancellingJobCard.id, cancelReason.trim());
      toast({ title: "Success", description: "Job card cancelled successfully" });
      setShowCancelDialog(false);
      setCancellingJobCard(null);
      setCancelReason('');
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to cancel job card", variant: "destructive" });
    }
  };

  const handleReactivateJobCard = async (jobCardId) => {
    try {
      await jobCardsAPI.reactivate(jobCardId);
      toast({ title: "Success", description: "Job card reactivated successfully" });
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to reactivate job card", variant: "destructive" });
    }
  };

  const openCancelDialog = (jc) => {
    setCancellingJobCard(jc);
    setCancelReason('');
    setShowCancelDialog(true);
  };

  const updatePreviewLine = (index, field, value) => {
    setEstimatePreview(prev => {
      const updated = { ...prev };
      const lines = [...updated.estimate_lines];
      lines[index] = { ...lines[index], [field]: value };
      lines[index].amount = lines[index].qty * lines[index].rate;
      updated.estimate_lines = lines;
      updated.total_amount = lines.reduce((sum, l) => sum + l.amount, 0);
      return updated;
    });
  };

  const openWorkDetails = (jobCard) => {
    setSelectedJobCard(jobCard);
    setShowWorkDetailsDialog(true);
  };

  const handleSaveWorkDetails = async () => {
    if (!workDetailsFormRef.current || !selectedJobCard) return;
    
    const formData = workDetailsFormRef.current.getData();
    const filledWorks = workDetailsFormRef.current.getFilledFinishingWorks();
    
    try {
      await jobCardsAPI.update(selectedJobCard.id, {
        cutting_qty_total: parseInt(formData.cutting_qty) || 0,
        pressing_qty_total: parseInt(formData.pressing_qty) || 0,
        finishing_lines: filledWorks.map(fw => ({
          description: fw.description,
          unit: fw.unit,
          qty: parseInt(fw.qty) || 0,
          rate: parseFloat(fw.rate) || 0,
          details: fw.details
        }))
      });
      
      toast({ title: "Success", description: "Work details saved" });
      setShowWorkDetailsDialog(false);
      setSelectedJobCard(null);
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to save work details", variant: "destructive" });
    }
  };

  // Client creation handler
  const handleCreateClient = async () => {
    if (!newClient.name || !newClient.mobile || !newClient.password) {
      toast({ title: "Error", description: "Name, Mobile and Password are required", variant: "destructive" });
      return;
    }
    
    try {
      await usersAPI.createClient({
        name: newClient.name,
        mobile: newClient.mobile,
        password: newClient.password,
        role: 'client',
        company: newClient.company,
        email: newClient.email
      });
      
      toast({ title: "Success", description: "Client created successfully" });
      setShowClientDialog(false);
      setNewClient({ name: '', mobile: '', password: '', company: '', email: '' });
      fetchData();
    } catch (error) {
      toast({ 
        title: "Error", 
        description: error.response?.data?.detail || "Failed to create client", 
        variant: "destructive" 
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-800';
      case 'in_production': return 'bg-emerald-100 text-emerald-800';
      case 'in_cutting': return 'bg-yellow-100 text-yellow-800';
      case 'in_pressing': return 'bg-orange-100 text-orange-800';
      case 'in_boring': return 'bg-purple-100 text-purple-800';
      case 'finishing': return 'bg-indigo-100 text-indigo-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'invoiced': return 'bg-emerald-100 text-emerald-800';
      case 'in_progress': return 'bg-amber-100 text-amber-800';
      case 'pending_supervisor_review': return 'bg-orange-100 text-orange-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      case 'sent_to_accounts': return 'bg-teal-100 text-teal-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const isDelayed = (jc) => {
    if (!jc.pushed_to_production_at) return false;
    if (['closed', 'completed', 'sent_to_accounts', 'pending_supervisor_review', 'cancelled'].includes(jc.status)) return false;
    const pushedDate = new Date(jc.pushed_to_production_at);
    const threeWeeksMs = 21 * 24 * 60 * 60 * 1000;
    return (Date.now() - pushedDate.getTime()) > threeWeeksMs;
  };

  // Stats dialog helpers
  const getStatsJobCards = () => {
    if (!statsDialogType) return [];
    switch (statsDialogType) {
      case 'active':
        return jobCards.filter(jc => !['completed', 'invoiced', 'closed', 'sent_to_accounts', 'cancelled'].includes(jc.status));
      case 'open':
        return jobCards.filter(jc => ['open', 'in_progress'].includes(jc.status));
      case 'completed':
        return jobCards.filter(jc => ['completed', 'invoiced', 'sent_to_accounts'].includes(jc.status));
      default:
        return [];
    }
  };

  const getStatsDialogTitle = () => {
    switch (statsDialogType) {
      case 'active': return 'Active Job Cards';
      case 'open': return 'Open Job Cards';
      case 'completed': return 'Completed Job Cards';
      default: return 'Job Cards';
    }
  };

  const getStatsDialogColor = () => {
    switch (statsDialogType) {
      case 'active': return 'text-blue-600';
      case 'open': return 'text-amber-600';
      case 'completed': return 'text-emerald-600';
      default: return 'text-gray-600';
    }
  };

  const handleStatCardClick = (type) => {
    setStatsDialogType(type);
    setShowStatsDialog(true);
  };

  const handleStatsJobCardClick = (jc) => {
    setShowStatsDialog(false);
    setViewJobCard(jc);
  };

  // Export handlers
  const handleExportJobCardPDF = async (jobCardId, jobCardNumber) => {
    try {
      const blob = await jobCardsAPI.exportPDF(jobCardId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `JobCard_${jobCardNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast({ title: "Success", description: "PDF downloaded" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to export PDF", variant: "destructive" });
    }
  };

  const handleExportJobCardExcel = async (jobCardId, jobCardNumber) => {
    try {
      const blob = await jobCardsAPI.exportExcel(jobCardId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `JobCard_${jobCardNumber}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast({ title: "Success", description: "Excel downloaded" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to export Excel", variant: "destructive" });
    }
  };

  const filterByDate = (createdAt) => {
    if (!createdAt) return true;
    if (dateFilter === 'all') return true;
    
    const itemDate = new Date(createdAt);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    switch (dateFilter) {
      case 'today':
        return itemDate >= today;
      case 'yesterday':
        const todayStart = new Date(today);
        return itemDate >= yesterday && itemDate < todayStart;
      case 'weekly':
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return itemDate >= weekAgo;
      case 'monthly':
        const monthAgo = new Date(today);
        monthAgo.setDate(monthAgo.getDate() - 30);
        return itemDate >= monthAgo;
      case 'custom':
        if (dateFilterStart && dateFilterEnd) {
          const start = new Date(dateFilterStart);
          start.setHours(0, 0, 0, 0);
          const end = new Date(dateFilterEnd);
          end.setHours(23, 59, 59, 999);
          return itemDate >= start && itemDate <= end;
        }
        return true;
      default:
        return true;
    }
  };

  const filteredProjects = projects.filter(p =>
    (p.project_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.job_card_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.reference?.toLowerCase().includes(searchTerm.toLowerCase())) &&
    filterByDate(p.created_at) &&
    (!showOnlyMine || p.created_by_id === user?.id)
  );

  const filteredJobCards = jobCards.filter(jc =>
    (jc.job_card_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    jc.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    jc.reference?.toLowerCase().includes(searchTerm.toLowerCase())) &&
    filterByDate(jc.created_at) &&
    (!showOnlyMine || jc.created_by_id === user?.id)
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden">
              <img src={appLogo} alt="Interior Job Work" className="w-[60px] h-[60px] object-cover" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Interior Job Work</h1>
              <p className="text-xs text-gray-500">Coordinator Panel</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600 hidden sm:block">Welcome, {user?.name}</span>
            <NotificationBell />
            <Button variant="ghost" size="sm" onClick={logout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid grid-cols-3 lg:w-[420px]">
            <TabsTrigger value="dashboard" className="flex items-center gap-2">
              <LayoutDashboard className="w-4 h-4" />
              <span className="hidden sm:inline">Dashboard</span>
            </TabsTrigger>
            <TabsTrigger value="jobcards" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Job Cards</span>
            </TabsTrigger>
            <TabsTrigger value="bom" className="flex items-center gap-2" data-testid="bom-tab">
              <Package className="w-4 h-4" />
              <span className="hidden sm:inline">BOM</span>
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Card 
                className="cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all"
                onClick={() => handleStatCardClick('active')}
                data-testid="coord-stat-card-active"
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Active Job Cards</p>
                      <p className="text-2xl font-bold text-blue-600">{stats?.total_active_projects || 0}</p>
                    </div>
                    <FolderKanban className="w-8 h-8 text-blue-200" />
                  </div>
                </CardContent>
              </Card>
              <Card 
                className="cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all"
                onClick={() => handleStatCardClick('open')}
                data-testid="coord-stat-card-open"
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Open Job Cards</p>
                      <p className="text-2xl font-bold text-amber-600">{stats?.open_job_cards || 0}</p>
                    </div>
                    <FileText className="w-8 h-8 text-amber-200" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Today's Cutting</p>
                      <p className="text-2xl font-bold text-green-600">{stats?.today_cutting_qty || 0}</p>
                    </div>
                    <Scissors className="w-8 h-8 text-green-200" />
                  </div>
                </CardContent>
              </Card>
              <Card 
                className="cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all"
                onClick={() => handleStatCardClick('completed')}
                data-testid="coord-stat-card-completed"
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Completed</p>
                      <p className="text-2xl font-bold text-emerald-600">{stats?.completed_projects || 0}</p>
                    </div>
                    <CheckCircle className="w-8 h-8 text-emerald-200" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* What's pending at each stage */}
            <FloorStatus jobCards={jobCards} onOpenCard={(jc) => setViewJobCard(jc)} />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Button className="w-full justify-start" onClick={() => setShowProjectDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create New Job Card
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Recent Job Cards</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {jobCards.slice(0, 4).map(jc => (
                      <div 
                        key={jc.id} 
                        className="flex items-center justify-between p-2 bg-gray-50 rounded cursor-pointer hover:bg-blue-50 hover:shadow-sm transition-all"
                        onClick={() => setViewJobCard(jc)}
                        data-testid={`dashboard-jc-${jc.job_card_number}`}
                      >
                        <div>
                          <p className="font-medium text-sm text-blue-700 underline decoration-dotted">{jc.job_card_number}</p>
                          <p className="text-xs text-gray-500">{jc.customer_name}{jc.reference ? ` · Ref: ${jc.reference}` : ''}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            {jc.created_by_name && (
                              <p className="text-xs text-blue-600">Coord: {jc.created_by_name}</p>
                            )}
                            {jc.created_at && (
                              <p className="text-xs text-gray-400 flex items-center gap-0.5">
                                <Clock className="w-2.5 h-2.5" />
                                {new Date(jc.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                              </p>
                            )}
                          </div>
                        </div>
                        <Badge className={getStatusColor(jc.status)}>{jc.status?.replace(/_/g, ' ')}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* BOM Tab */}
          <TabsContent value="bom" className="space-y-4" data-testid="bom-content">
            <BOMManager projects={projects} onDataChange={fetchData} />
          </TabsContent>

          {/* Job Cards Tab */}
          <TabsContent value="jobcards" className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3 justify-between">
              <div className="flex items-center gap-3 flex-1">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input placeholder="Search job card, client, reference..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
                </div>
                <DateFilter
                  value={dateFilter}
                  onChange={setDateFilter}
                  startDate={dateFilterStart}
                  endDate={dateFilterEnd}
                  onDateRangeChange={(start, end) => {
                    setDateFilterStart(start);
                    setDateFilterEnd(end);
                  }}
                />
                <Button
                  variant={showOnlyMine ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowOnlyMine(prev => !prev)}
                  className={showOnlyMine ? "bg-blue-600 hover:bg-blue-700 text-white" : ""}
                >
                  <Filter className="w-4 h-4 mr-1" /> My Cards
                </Button>
                {dateFilter !== 'all' && (
                  <Button variant="ghost" size="sm" onClick={() => { setDateFilter('all'); setDateFilterStart(null); setDateFilterEnd(null); }}>
                    Clear Date
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowClientDialog(true)}>
                  <UserPlus className="w-4 h-4 mr-2" /> Add Client
                </Button>
                <Button onClick={() => setShowProjectDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" /> New Job Card
                </Button>
              </div>
            </div>
            
            <div className="text-sm text-gray-500">
              Showing {filteredJobCards.length} job card{filteredJobCards.length !== 1 ? 's' : ''}
            </div>

            <div className="grid gap-3">
              {filteredJobCards.map(jc => {
                // Calculate task completion
                const tasks = jc.coordinator_tasks || [];
                const completedTasks = tasks.filter(t => t.status === 'completed').length;
                const totalTasks = 3;
                const isCreator = user?.id === jc.created_by_id;
                
                return (
                <Card key={jc.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setViewJobCard(jc)}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-pink-500 flex items-center justify-center">
                            <FileText className="w-4 h-4 text-white" />
                          </div>
                          <h3 className="font-semibold text-blue-700 cursor-pointer hover:underline" onClick={(e) => { e.stopPropagation(); setViewJobCard(jc); }}>{jc.job_card_number}</h3>
                          <Badge className={getStatusColor(jc.status)}>{jc.status?.replace(/_/g, ' ')}</Badge>
                          {isDelayed(jc) && (
                            <Badge className="bg-red-100 text-red-700 border border-red-300 animate-pulse" data-testid={`delayed-badge-${jc.job_card_number}`}>
                              Delayed
                            </Badge>
                          )}
                          {jc.return_reason && jc.status !== 'cancelled' && (
                            <Badge className="bg-amber-100 text-amber-700 border border-amber-300" title={`Returned from accounts: ${jc.return_reason}`} data-testid={`returned-badge-${jc.job_card_number}`}>
                              <CornerUpLeft className="w-3 h-3 mr-1" /> Returned
                            </Badge>
                          )}
                          {jc.reopen_reason && (jc.status === 'in_production' || jc.status === 'in_progress') && (
                            <Badge className="bg-amber-100 text-amber-700 border border-amber-300" title={`Back in production: ${jc.reopen_reason}`} data-testid={`reopened-badge-${jc.job_card_number}`}>
                              <RotateCcw className="w-3 h-3 mr-1" /> Back in production
                            </Badge>
                          )}
                          {/* Task Progress Badge */}
                          <Badge 
                            className={completedTasks === totalTasks 
                              ? 'bg-green-100 text-green-700' 
                              : completedTasks > 0 
                                ? 'bg-yellow-100 text-yellow-700' 
                                : 'bg-gray-100 text-gray-600'
                            }
                            title="Coordinator Tasks"
                          >
                            <ClipboardCheck className="w-3 h-3 mr-1" />
                            {completedTasks}/{totalTasks}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600 ml-10">{jc.customer_name}</p>
                        {jc.created_by_name && (
                          <p className="text-xs text-blue-600 ml-10">Coordinator: {jc.created_by_name}</p>
                        )}
                        {jc.reference && <p className="text-xs text-gray-500 ml-10">Ref: {jc.reference}</p>}
                        {jc.work_in_charge && <p className="text-xs text-gray-500 ml-10">In Charge: {jc.work_in_charge}</p>}
                        <div className="flex gap-4 mt-2 text-xs text-gray-500 ml-10">
                          <span><Scissors className="w-3 h-3 inline mr-1" />Cut: {jc.cutting_qty_total || 0}</span>
                          <PressingBalance jobCard={jc} variant="inline" />
                          <span><CircleDot className="w-3 h-3 inline mr-1" />Bore: {jc.boring_qty_total || 0}</span>
                          {jc.created_at && (
                            <span><Clock className="w-3 h-3 inline mr-1" />Created: {new Date(jc.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                          )}
                        </div>
                        {/* Operator Status Indicators */}
                        {(jc.status === 'in_production' || jc.status === 'in_progress') && (
                          <div className="flex flex-wrap gap-2 mt-1.5 ml-10">
                            {jc.cutting_status && (
                              <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${jc.cutting_status === 'completed' ? 'bg-green-100 text-green-700' : jc.cutting_status === 'started' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}>
                                Cutting: {jc.cutting_status.replace(/_/g, ' ')}
                              </span>
                            )}
                            {jc.boring_status && (
                              <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${jc.boring_status === 'completed' ? 'bg-green-100 text-green-700' : jc.boring_status === 'started' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}>
                                Boring: {jc.boring_status.replace(/_/g, ' ')}
                              </span>
                            )}
                            {(() => {
                              const allLines = [...(jc.normal_pressing_lines || []), ...(jc.hot_pressing_lines || []), ...(jc.veneer_pressing_lines || [])];
                              if (allLines.length === 0) return null;
                              const done = allLines.filter(l => l.status === 'completed').length;
                              const started = allLines.filter(l => l.status === 'started').length;
                              return (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${done === allLines.length ? 'bg-green-100 text-green-700' : (done + started) > 0 ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'}`}>
                                  Pressing: {done}/{allLines.length} done
                                </span>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <Button variant="outline" size="sm" onClick={() => setViewJobCard(jc)} title="View Details">
                          <Eye className="w-4 h-4" />
                        </Button>
                        {isCreator && (
                          <>
                            <Button variant="outline" size="sm" className="text-blue-600 border-blue-300" onClick={() => { setSelectedJobCard(jc); setPressingImages(jc.pressing_images || []); setShowPressingDialog(true); }} title="Edit Pressing">
                              <Layers className="w-4 h-4 mr-1" /> Pressing
                            </Button>
                            <Button variant="outline" size="sm" className="text-pink-600 border-pink-300" onClick={() => openWorkDetails(jc)} title="Edit Finishing">
                              <CircleDot className="w-4 h-4 mr-1" /> Finishing
                            </Button>
                          </>
                        )}
                        <Button variant="outline" size="sm" onClick={() => handleExportJobCardPDF(jc.id, jc.job_card_number)} title="Export PDF">
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleExportJobCardExcel(jc.id, jc.job_card_number)} title="Export Excel">
                          <FileSpreadsheet className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => printJobCard(jc)} title="Print" data-testid={`print-btn-${jc.job_card_number}`}>
                          <Printer className="w-4 h-4" />
                        </Button>
                        {isCreator && jc.status === 'open' && (
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => handlePushToProduction(jc.id)} data-testid={`push-to-production-${jc.job_card_number}`}>
                            Push to Production
                          </Button>
                        )}
                        {isCreator && (jc.status === 'in_production' || jc.status === 'in_progress') && (
                          <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => handleSendToBilling(jc.id)} data-testid={`send-to-billing-${jc.job_card_number}`}>
                            Send to Supervisor
                          </Button>
                        )}
                        {jc.status === 'pending_supervisor_review' && (
                          <Badge className="bg-orange-100 text-orange-800 animate-pulse">Pending Supervisor Review</Badge>
                        )}
                        {['pending_supervisor_review', 'completed', 'sent_to_accounts', 'closed'].includes(jc.status) && (
                          <Button variant="outline" size="sm" onClick={() => setBillJobCard(jc)} data-testid={`view-bill-${jc.job_card_number}`}>
                            <FileText className="w-4 h-4 mr-1" /> View bill
                          </Button>
                        )}
                        {jc.status === 'sent_to_accounts' && (
                          <Badge className="bg-purple-100 text-purple-800">Sent to Billing</Badge>
                        )}
                        {jc.status === 'completed' && (
                          <Badge className="bg-green-100 text-green-800">Completed</Badge>
                        )}
                        {jc.status === 'cancelled' && (
                          <>
                            <Badge className="bg-red-100 text-red-800">Cancelled</Badge>
                            {isCreator && (
                              <Button size="sm" variant="outline" className="text-green-600 border-green-300" onClick={() => handleReactivateJobCard(jc.id)} title="Reactivate Job Card">
                                <RotateCcw className="w-4 h-4 mr-1" /> Reactivate
                              </Button>
                            )}
                          </>
                        )}
                        {jc.supervisor_reject_reason && jc.status === 'in_progress' && (
                          <Badge className="bg-red-100 text-red-700" title={jc.supervisor_reject_reason}>Rejected by Supervisor</Badge>
                        )}
                        {/* Cancel button - show for non-completed, non-cancelled job cards */}
                        {isCreator && !['completed', 'closed', 'sent_to_accounts', 'cancelled'].includes(jc.status) && (
                          <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={() => openCancelDialog(jc)} title="Cancel Job Card" data-testid={`cancel-btn-${jc.job_card_number}`}>
                            <XCircle className="w-4 h-4 mr-1" /> Cancel
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
              })}
              {filteredJobCards.length === 0 && (
                <Card>
                  <CardContent className="p-8 text-center text-gray-500">
                    No job cards found. Create your first job card!
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Job Card Dialog */}
      <Dialog open={showProjectDialog} onOpenChange={(open) => {
        setShowProjectDialog(open);
        if (!open) {
          setShowInlineClientForm(false);
          setInlineNewClient({ name: '', mobile: '', company: '' });
          setClientSearchOpen(false);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Job Card</DialogTitle>
            <DialogDescription>Select a client and enter reference details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Client *</Label>
              {!showInlineClientForm ? (
                <div className="space-y-2">
                  <Popover open={clientSearchOpen} onOpenChange={setClientSearchOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" role="combobox" className="w-full justify-between font-normal">
                        {newProject.client_id
                          ? clients.find(c => c.id === newProject.client_id)?.name
                          : "Select client..."}
                        <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search client by name..." />
                        <CommandList>
                          <CommandEmpty>No client found.</CommandEmpty>
                          <CommandGroup>
                            {clients.map(c => (
                              <CommandItem
                                key={c.id}
                                value={c.name}
                                onSelect={() => {
                                  setNewProject(prev => ({ ...prev, client_id: c.id }));
                                  setClientSearchOpen(false);
                                }}
                              >
                                {c.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm" 
                    className="w-full text-green-600 border-green-300 hover:bg-green-50"
                    onClick={() => setShowInlineClientForm(true)}
                  >
                    <UserPlus className="w-4 h-4 mr-2" /> Add New Client
                  </Button>
                </div>
              ) : (
                <div className="space-y-3 p-3 border rounded-lg bg-green-50 border-green-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-green-700">New Client Details</span>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        setShowInlineClientForm(false);
                        setInlineNewClient({ name: '', mobile: '', company: '' });
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                  <div className="space-y-2">
                    <Input 
                      placeholder="Client Name *" 
                      value={inlineNewClient.name}
                      onChange={(e) => setInlineNewClient(prev => ({ ...prev, name: e.target.value }))}
                    />
                    <Input 
                      type="tel"
                      placeholder="Mobile Number *" 
                      value={inlineNewClient.mobile}
                      onChange={(e) => setInlineNewClient(prev => ({ ...prev, mobile: e.target.value }))}
                    />
                    <Input 
                      placeholder="Company (optional)" 
                      value={inlineNewClient.company}
                      onChange={(e) => setInlineNewClient(prev => ({ ...prev, company: e.target.value }))}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Reference</Label>
              <Input value={newProject.reference} onChange={(e) => setNewProject(prev => ({ ...prev, reference: e.target.value }))} placeholder="e.g., PO-12345" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={newProject.description} onChange={(e) => setNewProject(prev => ({ ...prev, description: e.target.value }))} placeholder="Project details..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProjectDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleCreateProject} 
              disabled={showInlineClientForm ? (!inlineNewClient.name || !inlineNewClient.mobile) : !newProject.client_id}
            >
              Create Job Card
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Job Card Dialog - Using Paper Form */}
      {/* Pressing Dialog */}
      <Dialog open={showPressingDialog} onOpenChange={(open) => {
        setShowPressingDialog(open);
        if (!open) setSelectedJobCard(null);
      }}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-blue-700">
              Edit Pressing - {selectedJobCard?.job_card_number}
            </DialogTitle>
            <DialogDescription>Fill out the pressing details (Normal Pressing, PVC & Acrylic)</DialogDescription>
          </DialogHeader>
          
          {selectedJobCard && (
            <JobCardPressingForm
              ref={jobCardFormRef}
              initialData={{
                job_card_number: selectedJobCard.job_card_number,
                customer: selectedJobCard.customer_name,
                reference: selectedJobCard.reference || '',
                mob_no: selectedJobCard.mobile_no || (() => {
                  const project = projects.find(p => p.id === selectedJobCard.project_id);
                  const client = project ? clients.find(c => c.id === project.client_id) : null;
                  return client?.mobile || '';
                })(),
                date: selectedJobCard.date || new Date().toISOString().split('T')[0],
                delivery_date: selectedJobCard.delivery_date || '',
                ref_job_card_no: selectedJobCard.ref_job_card_no || '',
                work_in_charge: selectedJobCard.work_in_charge || user?.name || '',
                // Map normal_pressing_lines from backend to hot_pressing_lines for form display
                // The form internally uses hot_pressing_lines for the "HOT PRESSING DETAILS" section
                hot_pressing_lines: selectedJobCard.normal_pressing_lines || [],
                veneer_pressing_lines: selectedJobCard.veneer_pressing_lines || []
              }}
            />
          )}

          {/* Hot Pressing Images Section */}
          {selectedJobCard && (
            <div className="border-2 border-gray-800 rounded-none font-mono text-sm mt-2">
              <div className="bg-orange-100 p-2 font-bold text-center border-b border-gray-800 flex justify-between items-center">
                <span className="flex items-center gap-2"><ImageIcon className="w-4 h-4" /> HOT PRESSING DIAGRAMS / IMAGES</span>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => pressingImageInputRef.current?.click()} 
                  className="h-7"
                  disabled={uploadingImage}
                  data-testid="add-pressing-image-btn"
                >
                  <Camera className="w-4 h-4 mr-1" /> {uploadingImage ? 'Uploading...' : 'Add Image'}
                </Button>
                <input
                  ref={pressingImageInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files[0]) handleUploadPressingImage(e.target.files[0]);
                  }}
                  data-testid="pressing-image-file-input"
                />
              </div>
              <div className="p-3">
                {pressingImages.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {pressingImages.map((url, idx) => (
                      <div key={url || idx} className="relative group border rounded overflow-hidden">
                        <img 
                          src={`${API_BASE}${url}`} 
                          alt={`Pressing diagram ${idx + 1}`}
                          className="w-full h-32 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => setViewingPressingImage(url)}
                          data-testid={`pressing-image-thumb-${idx}`}
                        />
                        <button
                          onClick={() => handleDeletePressingImage(url)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`delete-pressing-image-${idx}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-400 py-4 text-xs">No diagrams attached. Click "Add Image" to upload.</p>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPressingDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdatePressingJobCard} className="bg-blue-600 hover:bg-blue-700">
              Save Pressing Details
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Work Details Dialog - Pink Form */}
      <Dialog open={showWorkDetailsDialog} onOpenChange={(open) => {
        setShowWorkDetailsDialog(open);
        if (!open) setSelectedJobCard(null);
      }}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Work Details - {selectedJobCard?.job_card_number}</DialogTitle>
            <DialogDescription>Fill out finishing works for this job card</DialogDescription>
          </DialogHeader>
          
          {selectedJobCard && (
            <WorkDetailsForm 
              ref={workDetailsFormRef}
              initialData={{
                cutting_qty: selectedJobCard.cutting_qty_total?.toString() || '',
                pressing_qty: selectedJobCard.pressing_qty_total?.toString() || '',
                total_qty: ''
              }}
              existingFinishingLines={selectedJobCard.finishing_lines || []}
            />
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWorkDetailsDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveWorkDetails}>Save Work Details</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Job Card Dialog */}
      <Dialog open={!!viewJobCard} onOpenChange={() => setViewJobCard(null)}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Job Card - {viewJobCard?.job_card_number}</DialogTitle>
          </DialogHeader>
          {viewJobCard && (
            <div className="space-y-4">
              {/* Cancellation Notice */}
              {viewJobCard.status === 'cancelled' && viewJobCard.cancellation_reason && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2 text-red-700 font-medium mb-1">
                    <XCircle className="w-4 h-4" />
                    Job Card Cancelled
                  </div>
                  <p className="text-sm text-red-600"><strong>Reason:</strong> {viewJobCard.cancellation_reason}</p>
                  {viewJobCard.cancelled_by_name && (
                    <p className="text-xs text-red-500 mt-1">
                      Cancelled by {viewJobCard.cancelled_by_name} on {viewJobCard.cancelled_at ? new Date(viewJobCard.cancelled_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                    </p>
                  )}
                </div>
              )}

              {/* Back-in-production Notice */}
              {viewJobCard.reopen_reason && (viewJobCard.status === 'in_production' || viewJobCard.status === 'in_progress') && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-2 text-amber-700 font-medium mb-1">
                    <RotateCcw className="w-4 h-4" />
                    Back in production
                  </div>
                  <p className="text-sm text-amber-700"><strong>Reason:</strong> {viewJobCard.reopen_reason}</p>
                  <p className="text-xs text-amber-600 mt-1">
                    Sent back by {viewJobCard.reopened_by || 'the supervisor'}{viewJobCard.reopened_at ? ` on ${new Date(viewJobCard.reopened_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}` : ''} — once the remaining work is done, send it to the supervisor again.
                  </p>
                </div>
              )}

              {/* Returned-from-accounts Notice */}
              {viewJobCard.return_reason && viewJobCard.status !== 'cancelled' && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-2 text-amber-700 font-medium mb-1">
                    <CornerUpLeft className="w-4 h-4" />
                    Returned from Accounts
                  </div>
                  <p className="text-sm text-amber-700"><strong>Reason:</strong> {viewJobCard.return_reason}</p>
                  {viewJobCard.returned_from_accounts_by && (
                    <p className="text-xs text-amber-600 mt-1">
                      Returned by {viewJobCard.returned_from_accounts_by} on {viewJobCard.returned_from_accounts_at ? new Date(viewJobCard.returned_from_accounts_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'} — make your changes, then send to accounts again.
                    </p>
                  )}
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Customer</Label>
                  {editingClientName ? (
                    <div className="flex items-center gap-1 mt-0.5">
                      <Input
                        value={editClientNameValue}
                        onChange={(e) => setEditClientNameValue(e.target.value)}
                        className="h-8 text-sm"
                        autoFocus
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSaveClientName(); if (e.key === 'Escape') setEditingClientName(false); }}
                      />
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-green-600" onClick={handleSaveClientName}><Save className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-gray-400" onClick={() => setEditingClientName(false)}><X className="w-4 h-4" /></Button>
                    </div>
                  ) : (
                    <p className="font-medium flex items-center gap-1">
                      {viewJobCard.customer_name}
                      <button className="text-gray-400 hover:text-blue-600" onClick={() => { setEditClientNameValue(viewJobCard.customer_name || ''); setEditingClientName(true); }}><Edit className="w-3.5 h-3.5" /></button>
                    </p>
                  )}
                </div>
                <div><Label className="text-gray-500">Coordinator</Label><p className="font-medium text-blue-600">{viewJobCard.created_by_name || '-'}</p></div>
                <div><Label className="text-gray-500">Status</Label><Badge className={getStatusColor(viewJobCard.status)}>{viewJobCard.status?.replace(/_/g, ' ')}</Badge></div>
                <div><Label className="text-gray-500">Work In Charge</Label><p className="font-medium">{viewJobCard.work_in_charge || '-'}</p></div>
                <div><Label className="text-gray-500">Reference</Label><p className="font-medium">{viewJobCard.reference || '-'}</p></div>
                <div><Label className="text-gray-500">Mobile</Label><p className="font-medium">{viewJobCard.mobile_no || '-'}</p></div>
                <div><Label className="text-gray-500">Date</Label><p className="font-medium">{viewJobCard.created_at ? new Date(viewJobCard.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</p></div>
                <div><Label className="text-gray-500">Delivery Date</Label><p className="font-medium">{viewJobCard.delivery_date ? new Date(viewJobCard.delivery_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</p></div>
              </div>
              
              <PressingBalance jobCard={viewJobCard} variant="full" className="pt-4 border-t" />

              <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                <div className="p-3 bg-blue-50 rounded text-center">
                  <p className="text-2xl font-bold text-blue-600">{viewJobCard.cutting_qty_total || 0}</p>
                  <p className="text-xs text-gray-600">Cutting Total</p>
                </div>
                <div className="p-3 bg-amber-50 rounded text-center">
                  <p className="text-2xl font-bold text-amber-600">{viewJobCard.pressing_qty_total || 0}</p>
                  <p className="text-xs text-gray-600">Pressing Total</p>
                </div>
                <div className="p-3 bg-purple-50 rounded text-center">
                  <p className="text-2xl font-bold text-purple-600">{viewJobCard.boring_qty_total || 0}</p>
                  <p className="text-xs text-gray-600">Boring Total</p>
                </div>
              </div>

              {viewJobCard.normal_pressing_lines?.length > 0 && (
                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-2">Normal Pressing Lines</h4>
                  <table className="w-full text-sm border">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="border p-2">Description</th>
                        <th className="border p-2">Ply Qty</th>
                        <th className="border p-2">Side</th>
                        <th className="border p-2">Total</th>
                        <th className="border p-2">Details</th>
                        <th className="border p-2">Status</th>
                        <th className="border p-2">Machine</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewJobCard.normal_pressing_lines.map((line, idx) => (
                        <tr key={line.id || idx} className={line.highlighted ? 'bg-green-100 border-l-4 border-l-green-500' : line.status === 'completed' ? 'bg-green-50' : ''}>
                          <td className="border p-2">
                            <span className="inline-flex items-center gap-1">
                              {line.highlighted && <Highlighter className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />}
                              {line.description}
                            </span>
                          </td>
                          <td className="border p-2 text-center">{line.ply_qty}</td>
                          <td className="border p-2 text-center">{line.side}</td>
                          <td className="border p-2 text-center font-bold">{line.total}</td>
                          <td className="border p-2">{line.details || '-'}</td>
                          <td className="border p-2 text-center">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${line.status === 'completed' ? 'bg-green-100 text-green-700' : line.status === 'started' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}>
                              {line.status?.replace(/_/g, ' ') || 'not started'}
                            </span>
                          </td>
                          <td className="border p-2 text-center text-xs">
                            {line.machine_type ? (line.machine_type === 'hot' ? 'HOT' : 'COLD') : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {viewJobCard.veneer_pressing_lines?.length > 0 && (
                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-2">Veneer/PVC/Acrylic Pressing Lines</h4>
                  <table className="w-full text-sm border">
                    <thead className="bg-amber-50">
                      <tr>
                        <th className="border p-2">Description</th>
                        <th className="border p-2">Ply Qty</th>
                        <th className="border p-2">Side</th>
                        <th className="border p-2">Total</th>
                        <th className="border p-2">Details</th>
                        <th className="border p-2">Status</th>
                        <th className="border p-2">Machine</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewJobCard.veneer_pressing_lines.map((line, idx) => (
                        <tr key={line.id || idx} className={line.highlighted ? 'bg-green-100 border-l-4 border-l-green-500' : line.status === 'completed' ? 'bg-green-50' : ''}>
                          <td className="border p-2">
                            <span className="inline-flex items-center gap-1">
                              {line.highlighted && <Highlighter className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />}
                              {line.description}
                            </span>
                          </td>
                          <td className="border p-2 text-center">{line.ply_qty}</td>
                          <td className="border p-2 text-center">{line.side}</td>
                          <td className="border p-2 text-center font-bold">{line.total}</td>
                          <td className="border p-2">{line.details || '-'}</td>
                          <td className="border p-2 text-center">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${line.status === 'completed' ? 'bg-green-100 text-green-700' : line.status === 'started' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}>
                              {line.status?.replace(/_/g, ' ') || 'not started'}
                            </span>
                          </td>
                          <td className="border p-2 text-center text-xs">
                            {line.machine_type ? (line.machine_type === 'hot' ? 'HOT' : 'COLD') : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pressing Diagrams/Images in View */}
              {viewJobCard.pressing_images?.length > 0 && (
                <div className="pt-4 border-t" data-testid="coordinator-view-pressing-images">
                  <h4 className="font-medium mb-2 flex items-center gap-1">
                    <ImageIcon className="w-4 h-4" /> Pressing Diagrams ({viewJobCard.pressing_images.length})
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {viewJobCard.pressing_images.map((url, idx) => (
                      <img 
                        key={url || idx}
                        src={`${API_BASE}${url}`} 
                        alt={`Pressing diagram ${idx + 1}`}
                        className="w-full h-28 object-cover rounded border cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => setViewingPressingImage(url)}
                        data-testid={`coordinator-view-pressing-image-${idx}`}
                      />
                    ))}
                  </div>
                </div>
              )}

              {viewJobCard.finishing_lines?.length > 0 && (
                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-2">Finishing Works</h4>
                  <table className="w-full text-sm border">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="border p-2">Description</th>
                        <th className="border p-2">Qty</th>
                        <th className="border p-2">Unit</th>
                        <th className="border p-2">Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewJobCard.finishing_lines.map((line, idx) => (
                        <tr key={`row-${idx}`}>
                          <td className="border p-2">{line.description}</td>
                          <td className="border p-2 text-center">{line.qty}</td>
                          <td className="border p-2 text-center">{line.unit}</td>
                          <td className="border p-2 text-center">{line.rate}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Coordinator Tasks Section */}
              <div className="pt-4 border-t" id="coordinator-tasks-section">
                <CoordinatorTaskManager 
                  jobCard={viewJobCard} 
                  coordinatorName={user?.full_name || user?.name || 'Coordinator'}
                  onUpdate={async () => {
                    // Refresh both the list and the open dialog so user sees instant update
                    const updated = await jobCardsAPI.getById(viewJobCard.id);
                    setViewJobCard(updated);
                    fetchData();
                  }}
                />
              </div>
            </div>
          )}
          {viewJobCard && (
            <div className="mt-4 space-y-3">
              <WorkLogHistory jobCardId={viewJobCard.id} />
              <OperatorEditHistory jobCardId={viewJobCard.id} />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewJobCard(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Client Dialog */}
      <Dialog open={showClientDialog} onOpenChange={setShowClientDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Client</DialogTitle>
            <DialogDescription>Create a new client account</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={newClient.name}
                onChange={(e) => setNewClient(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Client/Company name"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Mobile Number *</Label>
              <Input
                type="tel"
                value={newClient.mobile}
                onChange={(e) => setNewClient(prev => ({ ...prev, mobile: e.target.value }))}
                placeholder="10-digit mobile number"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Password *</Label>
              <Input
                type="password"
                value={newClient.password}
                onChange={(e) => setNewClient(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Set password for client"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Company</Label>
              <Input
                value={newClient.company}
                onChange={(e) => setNewClient(prev => ({ ...prev, company: e.target.value }))}
                placeholder="Company name"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Email (optional)</Label>
              <Input
                type="email"
                value={newClient.email}
                onChange={(e) => setNewClient(prev => ({ ...prev, email: e.target.value }))}
                placeholder="client@example.com"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClientDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateClient}>Create Client</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Full Pressing Image Dialog */}
      <Dialog open={!!viewingPressingImage} onOpenChange={() => setViewingPressingImage(null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="view-pressing-image-dialog">
          <DialogHeader>
            <DialogTitle>Pressing Diagram</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-2">
            {viewingPressingImage && (
              <img 
                src={`${API_BASE}${viewingPressingImage}`} 
                alt="Pressing diagram" 
                className="max-h-[60vh] w-auto rounded-lg object-contain"
                data-testid="pressing-image-full"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Read-only bill after sending */}
      <BillDialog jobCard={billJobCard} onClose={() => setBillJobCard(null)} />

      {/* Estimate Preview Dialog */}
      <Dialog open={showEstimatePreview} onOpenChange={(open) => { if (!open) { setShowEstimatePreview(false); setEstimatePreview(null); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Estimate Preview - {estimatePreview?.job_card_number}</span>
              <Badge variant="outline">{estimatePreview?.customer_name}</Badge>
            </DialogTitle>
          </DialogHeader>
          {estimatePreview && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">Review and edit rates before sending to accountant. Rates are auto-filled from the Rate Chart.</p>
              <table className="w-full text-sm border" data-testid="estimate-preview-table">
                <thead className="bg-blue-50">
                  <tr>
                    <th className="border p-2 text-left">Item</th>
                    <th className="border p-2 text-center w-20">Qty</th>
                    <th className="border p-2 text-center w-16">Unit</th>
                    <th className="border p-2 text-center w-24">Rate</th>
                    <th className="border p-2 text-right w-28">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {estimatePreview.estimate_lines.map((line, idx) => (
                    <tr key={`row-${idx}`}>
                      <td className="border p-2">
                        <div className="flex items-center gap-2">
                          <span>{String(line.particular || '').toUpperCase()}</span>
                          {line.breakdown && line.breakdown.length > 0 && (
                            <button
                              type="button"
                              className="text-xs text-blue-600 hover:text-blue-800 hover:underline whitespace-nowrap"
                              onClick={() => setBreakdownLine(line)}
                              data-testid={`view-breakdown-${idx}`}
                            >
                              View breakdown ({line.breakdown.length})
                            </button>
                          )}
                          {unpricedCount(line) > 0 && (
                            <span className="text-xs text-red-600 font-medium whitespace-nowrap" data-testid={`unpriced-warning-${idx}`}>
                              {unpricedCount(line)} item{unpricedCount(line) > 1 ? 's' : ''} not in rate chart
                            </span>
                          )}
                          {defaultedCount(line) > 0 && (
                            <span className="text-xs text-amber-600 whitespace-nowrap" data-testid={`defaulted-note-${idx}`}>
                              {defaultedCount(line)} on default rate
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="border p-2 text-center">
                        <input
                          type="number"
                          value={line.qty}
                          onChange={(e) => updatePreviewLine(idx, 'qty', parseFloat(e.target.value) || 0)}
                          onWheel={(e) => e.target.blur()}
                          className="w-16 text-center border rounded px-1 py-0.5 text-sm"
                          data-testid={`preview-qty-${idx}`}
                        />
                      </td>
                      <td className="border p-2 text-center">{line.unit || ''}</td>
                      <td className="border p-2 text-center">
                        <input
                          type="number"
                          value={line.rate}
                          onChange={(e) => updatePreviewLine(idx, 'rate', parseFloat(e.target.value) || 0)}
                          onWheel={(e) => e.target.blur()}
                          className={`w-20 text-center border rounded px-1 py-0.5 text-sm ${isUnpriced(line) ? 'border-red-300 bg-red-50' : ''}`}
                          data-testid={`preview-rate-${idx}`}
                        />
                      </td>
                      <td className="border p-2 text-right font-medium">
                        {line.amount.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 font-bold">
                    <td className="border p-2" colSpan="4">Grand Total</td>
                    <td className="border p-2 text-right text-blue-700" data-testid="estimate-preview-total">
                      Rs {estimatePreview.total_amount.toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
              {estimatePreview.estimate_lines.some(isUnpriced) && (
                <p className="text-xs text-red-500">
                  Some items are not in the Rate Chart. Check the highlighted rows (open "View breakdown" to see which
                  materials) and enter the rate, or add them to the Rate Chart.
                </p>
              )}
              {sendPressingLeft > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg text-sm text-amber-800" data-testid="pressing-pending-warning">
                  <strong>Pressing isn't finished:</strong> {sendPressingLeft} of {sendPressing.planned} still to press.
                  Sending it for review takes this card off the pressing team's list. Send anyway?
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => { setShowEstimatePreview(false); setEstimatePreview(null); }}>Cancel</Button>
                <Button
                  onClick={handleConfirmSendToAccounts}
                  disabled={sendingEstimate}
                  className={sendPressingLeft > 0 ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''}
                  data-testid="confirm-send-billing-btn"
                >
                  {sendingEstimate ? 'Sending...' : sendPressingLeft > 0 ? `Send anyway (${sendPressingLeft} still to press)` : 'Confirm & Send to Accountant'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Estimate Line Breakdown sub-dialog (for collated pressing lines) */}
      <Dialog open={!!breakdownLine} onOpenChange={() => setBreakdownLine(null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="breakdown-dialog">
          <DialogHeader>
            <DialogTitle>Breakdown — {breakdownLine?.particular}</DialogTitle>
            <DialogDescription>
              Original detail lines from the job card that were collated into this single estimate row.
            </DialogDescription>
          </DialogHeader>
          {breakdownLine && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border">
                <thead className="bg-blue-50">
                  <tr>
                    <th className="border p-2 text-left">#</th>
                    <th className="border p-2 text-left">Description</th>
                    <th className="border p-2 text-center w-16">Ply</th>
                    <th className="border p-2 text-center w-16">Side</th>
                    <th className="border p-2 text-center w-20">Qty</th>
                    <th className="border p-2 text-center w-20">Rate</th>
                    <th className="border p-2 text-right w-24">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(breakdownLine.breakdown || []).map((b, i) => (
                    <tr key={i} data-testid={`breakdown-row-${i}`} className={!b.rate ? 'bg-red-50' : b.defaulted ? 'bg-amber-50' : ''}>
                      <td className="border p-2 text-center">{i + 1}</td>
                      <td className="border p-2">
                        {b.description}
                        {!b.rate ? (
                          <span className="ml-2 text-xs text-red-600 font-medium">not in rate chart</span>
                        ) : b.defaulted ? (
                          <span className="ml-2 text-xs text-amber-600" title="Not in the rate chart — billed at the category's default rate">default rate</span>
                        ) : null}
                      </td>
                      <td className="border p-2 text-center">{b.ply_qty}</td>
                      <td className="border p-2 text-center">{b.side}</td>
                      <td className="border p-2 text-center">{b.qty}</td>
                      <td className={`border p-2 text-center ${!b.rate ? 'text-red-600 font-medium' : b.defaulted ? 'text-amber-700' : ''}`}>{Number(b.rate).toFixed(2)}</td>
                      <td className="border p-2 text-right font-medium">{Number(b.amount).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 font-bold">
                    <td className="border p-2 text-right" colSpan="4">Total</td>
                    <td className="border p-2 text-center">{breakdownLine.qty}</td>
                    <td className="border p-2"></td>
                    <td className="border p-2 text-right text-blue-700">{Number(breakdownLine.amount ?? (breakdownLine.qty * breakdownLine.rate)).toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Stats Dialog - Shows filtered job cards */}
      <Dialog open={showStatsDialog} onOpenChange={setShowStatsDialog}>
        <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="coord-stats-dialog">
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${getStatsDialogColor()}`}>
              {statsDialogType === 'active' && <FolderKanban className="w-5 h-5" />}
              {statsDialogType === 'open' && <FileText className="w-5 h-5" />}
              {statsDialogType === 'completed' && <CheckCircle className="w-5 h-5" />}
              {getStatsDialogTitle()}
              <Badge variant="secondary" className="ml-2">{getStatsJobCards().length}</Badge>
            </DialogTitle>
            <DialogDescription>
              {statsDialogType === 'active' && 'Job cards currently in production'}
              {statsDialogType === 'open' && 'Job cards with open or in-progress status'}
              {statsDialogType === 'completed' && 'Job cards that have been completed'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-2 mt-4">
            {getStatsJobCards().length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No job cards in this category</p>
              </div>
            ) : (
              getStatsJobCards().map(jc => {
                const project = projects.find(p => p.id === jc.project_id);
                return (
                  <div
                    key={jc.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-blue-50 hover:shadow-sm transition-all border"
                    onClick={() => handleStatsJobCardClick(jc)}
                    data-testid={`coord-stats-jc-${jc.job_card_number}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <p className="font-semibold text-blue-700 underline decoration-dotted text-lg">
                          #{jc.job_card_number}
                        </p>
                        <Badge className={getStatusColor(jc.status)}>
                          {jc.status?.replace(/_/g, ' ')}
                        </Badge>
                        {isDelayed(jc) && (
                          <Badge className="bg-red-100 text-red-700 border border-red-300 animate-pulse">
                            Delayed
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{project?.client_name || jc.client_name || 'Unknown Client'}</p>
                      {jc.created_at && (
                        <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Created: {new Date(jc.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="grid grid-cols-3 gap-3 text-xs">
                        <div className="text-center">
                          <p className="font-bold text-blue-600">{jc.cutting_qty_total || 0}</p>
                          <p className="text-gray-500">Cutting</p>
                        </div>
                        <div className="text-center">
                          <p className="font-bold text-amber-600">{jc.pressing_qty_total || 0}</p>
                          <p className="text-gray-500">Pressing</p>
                        </div>
                        <div className="text-center">
                          <p className="font-bold text-purple-600">{jc.boring_qty_total || 0}</p>
                          <p className="text-gray-500">Boring</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowStatsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Job Card Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={(open) => { setShowCancelDialog(open); if (!open) { setCancellingJobCard(null); setCancelReason(''); } }}>
        <DialogContent className="sm:max-w-md" data-testid="cancel-job-card-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <XCircle className="w-5 h-5" />
              Cancel Job Card #{cancellingJobCard?.job_card_number}
            </DialogTitle>
            <DialogDescription>
              This will temporarily cancel the job card. You can reactivate it later if needed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Cancellation Reason *</label>
              <Textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Enter reason for cancellation (e.g., Created by mistake, Project paused, Customer request...)"
                className="mt-1 min-h-[100px]"
                data-testid="cancel-reason-input"
              />
            </div>
            {cancellingJobCard?.customer_name && (
              <div className="text-sm text-gray-500">
                <strong>Customer:</strong> {cancellingJobCard.customer_name}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCancelDialog(false); setCancellingJobCard(null); setCancelReason(''); }}>
              Keep Job Card
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleCancelJobCard} 
              disabled={!cancelReason.trim()}
              data-testid="confirm-cancel-btn"
            >
              <XCircle className="w-4 h-4 mr-2" />
              Cancel Job Card
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CoordinatorDashboard;
